export async function base64ToFile(base64: string, filename: string, mimeType: string): Promise<File> {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], filename, { type: mimeType });
}

export function downloadFile(url: string, filename: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

// Convert PDF to JPG using pdf.js
export async function processPdfToJpg(files: File[], updateProgress: (text: string) => void) {
    const pdfjsLib = (window as any).pdfjsLib;
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        updateProgress(`Converting ${file.name} to JPG...`);

        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 2 });
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (!context) continue;
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            await page.render({ canvasContext: context, viewport: viewport }).promise;
            const imgData = canvas.toDataURL("image/jpeg");
            const blob = await (await fetch(imgData)).blob();
            const imgUrl = URL.createObjectURL(blob);

            downloadFile(imgUrl, `${file.name.replace(/\.(pdf|PDF)$/, "")}_page_${pageNum}.jpg`);
            
            setTimeout(() => { URL.revokeObjectURL(imgUrl); }, 1000);
        }
    }
}

// Convert PDF to PNG using pdf.js
export async function processPdfToPng(files: File[], updateProgress: (text: string) => void) {
    const pdfjsLib = (window as any).pdfjsLib;
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        updateProgress(`Converting ${file.name} to PNG...`);

        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 2 });
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (!context) continue;
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            await page.render({ canvasContext: context, viewport: viewport }).promise;
            const imgData = canvas.toDataURL("image/png");
            const blob = await (await fetch(imgData)).blob();
            const imgUrl = URL.createObjectURL(blob);

            downloadFile(imgUrl, `${file.name.replace(/\.(pdf|PDF)$/, "")}_page_${pageNum}.png`);
            
            setTimeout(() => { URL.revokeObjectURL(imgUrl); }, 1000);
        }
    }
}

// Compress PDF
export async function processCompressPdf(files: File[], updateProgress: (text: string) => void) {
    const PDFLib = (window as any).PDFLib;
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        updateProgress(`Compressing ${file.name}...`);

        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFLib.PDFDocument.load(arrayBuffer);

        const compressedPdfBytes = await pdfDoc.save();

        const blob = new Blob([compressedPdfBytes], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(blob);

        downloadFile(pdfUrl, file.name.replace(/\.(pdf|PDF)$/, "_compressed.pdf"));

        setTimeout(() => { URL.revokeObjectURL(pdfUrl); }, 1000);
    }
}

// Extract text from PDF
export async function extractPdfText(file: File): Promise<string> {
    const pdfjsLib = (window as any).pdfjsLib;
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = "";

    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item: any) => item.str).join(" ");
        fullText += pageText + "\\n";
    }
    return fullText;
}

// Central executor
export async function executePdfOperation(prompt: string, attachments: any[], updateProgress: (msg: string) => void): Promise<string | null> {
    const pdfAttachments = attachments.filter(a => a.type === 'application/pdf');
    if (pdfAttachments.length === 0) return null;

    const files: File[] = [];
    for (const att of pdfAttachments) {
        files.push(await base64ToFile(att.data, att.name, att.type));
    }

    const lowerPrompt = prompt.toLowerCase();
    
    if (lowerPrompt.includes('convert') && lowerPrompt.includes('jpg')) {
        await processPdfToJpg(files, updateProgress);
        return "I have successfully converted the PDF(s) to JPG. The files should be downloading now.";
    }
    
    if (lowerPrompt.includes('convert') && lowerPrompt.includes('png')) {
        await processPdfToPng(files, updateProgress);
        return "I have successfully converted the PDF(s) to PNG. The files should be downloading now.";
    }
    
    if (lowerPrompt.includes('compress')) {
        await processCompressPdf(files, updateProgress);
        return "I have successfully compressed the PDF(s). The files should be downloading now.";
    }

    return null;
}
